class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindDelegatedEvents();
    this.ensureShopGuardPersisted();
    this.bindUpsellDrag();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Use a single delegated listener on the drawer root so handlers
   * survive innerHTML replacement on cart updates.
   */
  bindDelegatedEvents() {
    this.addEventListener('submit', (event) => {
      const upsellForm = event.target.closest('[data-cart-upsell-form]');
      if (upsellForm) {
        event.preventDefault();
        this.handleUpsellAdd(upsellForm);
      }
    });

    this.addEventListener('click', (event) => {
      const promoApply = event.target.closest('[data-cart-promo-apply]');
      if (promoApply) {
        event.preventDefault();
        this.handlePromoApply();
      }
    });

    this.addEventListener('change', (event) => {
      const shopguardToggle = event.target.closest('[data-shopguard-toggle]');
      if (shopguardToggle) {
        this.handleShopGuardToggle(shopguardToggle);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.ensureShopGuardPersisted();
      this.bindUpsellDrag();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ---------- Upsell add-to-cart ---------- */

  async handleUpsellAdd(form) {
    const button = form.querySelector('.cart-drawer__upsell-add');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      formData.append(
        'sections',
        this.getSectionsToRender().map((s) => s.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add item');
      }

      // Briefly show success state before re-render
      button.classList.remove('is-loading');
      button.classList.add('is-added');

      if (data.sections) {
        this.renderContents({
          id: data.id,
          sections: data.sections,
        });
      } else {
        await this.refreshSections();
        this.open();
      }
    } catch (err) {
      console.error('[cart-drawer] upsell add failed', err);
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }

  async refreshSections() {
    const ids = this.getSectionsToRender().map((s) => s.id).join(',');
    const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`);
    const sections = await res.json();
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });
  }

  /* ---------- ShopGuard toggle ---------- */

  async handleShopGuardToggle(toggle) {
    const value = toggle.checked ? 'true' : 'false';
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ attributes: { shopguard_toggle: value } }),
      });
      if (!res.ok) throw new Error('shopguard update failed');
      toggle.dataset.shopguardPersisted = 'true';
    } catch (err) {
      console.error('[cart-drawer] shopguard toggle failed', err);
      // Revert visual state so it matches the actual cart attribute
      toggle.checked = !toggle.checked;
    }
  }

  ensureShopGuardPersisted() {
    const toggle = this.querySelector('[data-shopguard-toggle]');
    if (!toggle) return;
    if (toggle.dataset.shopguardPersisted === 'true') return;
    // First render with no attribute on the cart yet — persist the default state
    // so Shopify Flow has the value at order-create time.
    this.handleShopGuardToggle(toggle);
  }

  /* ---------- Drag-to-scroll on upsell carousel ---------- */

  bindUpsellDrag() {
    const scroll = this.querySelector('[data-upsell-scroll]');
    if (!scroll || scroll.dataset.dragBound === 'true') return;
    scroll.dataset.dragBound = 'true';

    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    const DRAG_THRESHOLD = 6;

    const onPointerDown = (e) => {
      // Native touch-scroll handles touch; only intercept mouse/pen.
      if (e.pointerType === 'touch') return;
      isDown = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = scroll.scrollLeft;
      scroll.setPointerCapture(e.pointerId);
      scroll.classList.add('is-grabbing');
    };

    const onPointerMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
      scroll.scrollLeft = startScrollLeft - dx;
    };

    const onPointerUp = (e) => {
      if (!isDown) return;
      isDown = false;
      scroll.classList.remove('is-grabbing');
      try { scroll.releasePointerCapture(e.pointerId); } catch (_) {}
    };

    // If a drag occurred, swallow the resulting click so cards don't navigate.
    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    scroll.addEventListener('pointerdown', onPointerDown);
    scroll.addEventListener('pointermove', onPointerMove);
    scroll.addEventListener('pointerup', onPointerUp);
    scroll.addEventListener('pointercancel', onPointerUp);
    scroll.addEventListener('click', onClickCapture, true);
    // Avoid native image-drag interfering
    scroll.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------- Promo code ---------- */

  handlePromoApply() {
    const input = this.querySelector('#CartDrawer-Promo');
    if (!input) return;
    const code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }
    // Shopify's standard pattern: hitting /discount/CODE applies the discount
    // to the current session/cart and redirects to the path provided.
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
